import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import { useAuth } from '../../../context/AuthContext';
import api from "../../../api/axiosConfig";

const Dashboard = () => {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [siteData, setSiteData] = useState(null);
    const [stats, setStats] = useState({
        totalPosts: 0,
        publishedPosts: 0,
        draftPosts: 0,
        totalViews: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchDashboardData();
    }, [user?.site_id]);

    const fetchDashboardData = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            
            setError('');
            const siteId = user?.site_id;
            
            if (!siteId) {
                setError('Site ID not found. Please contact support.');
                return;
            }

            // Fetch site data
            const siteResponse = await api.get(`/sites/${siteId}`);
            if (siteResponse.data) {
                setSiteData(siteResponse.data);
            }

            // Fetch posts for the site
            const postsResponse = await api.get(`/posts?site_id=${siteId}`);
            if (postsResponse.data.success) {
                const postsData = postsResponse.data.data || [];
                setPosts(postsData.slice(0, 5)); // Show only recent 5 posts

                // Calculate stats from real data
                const totalPosts = postsData.length;
                const publishedPosts = postsData.filter(post => post.status === 1).length;
                const draftPosts = postsData.filter(post => post.status === 0).length;
                const totalViews = postsData.reduce((sum, post) => sum + (post.views || 0), 0);

                setStats({
                    totalPosts,
                    publishedPosts,
                    draftPosts,
                    totalViews
                });
            } else {
                setError('Failed to fetch posts data.');
            }
        } catch (err) {
            console.error('Dashboard data fetch error:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleViewSite = () => {
        if (siteData && siteData.http_link) {
            let url = siteData.http_link;
            // Ensure the URL has proper protocol
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            setError('Website URL not available.');
        }
    };

    const handleViewPost = (post) => {
        if (siteData && siteData.http_link && post.status === 1) {
            let baseUrl = siteData.http_link;
            if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                baseUrl = 'https://' + baseUrl;
            }
            // You might want to append the post slug or ID to the URL
            // This depends on your frontend routing structure
            const postUrl = `${baseUrl}/posts/${post.id}`; // Adjust this based on your frontend routes
            window.open(postUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case 1: return 'success';
            case 0: return 'secondary';
            default: return 'secondary';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 1: return 'Published';
            case 0: return 'Draft';
            default: return 'Draft';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleRefresh = () => {
        fetchDashboardData(true);
    };

    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        
        if (hour >= 5 && hour < 12) {
            return 'Welcome back this morning';
        } else if (hour >= 12 && hour < 17) {
            return 'Welcome back this afternoon';
        } else if (hour >= 17 && hour < 21) {
            return 'Welcome back this evening';
        } else {
            return 'Welcome back';
        }
    };

    // Real dashboard stats based on actual data
    const dashboardStats = [
        {
            title: 'Total Posts',
            value: stats.totalPosts,
            icon: 'fa-newspaper',
            color: 'primary',
            description: 'All time posts',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        {
            title: 'Published',
            value: stats.publishedPosts,
            icon: 'fa-check-circle',
            color: 'success',
            description: 'Live posts',
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
        },
        {
            title: 'Drafts',
            value: stats.draftPosts,
            icon: 'fa-edit',
            color: 'warning',
            description: 'In progress',
            gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
        },
        {
            title: 'Total Views',
            value: stats.totalViews,
            icon: 'fa-eye',
            color: 'info',
            description: 'All post views',
            gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
        }
    ];

    if (loading) {
        return (
            <Container fluid className="dashboard-container">
                <div className="loading-container">
                    <div className="loading-spinner">
                        <Spinner animation="border" variant="primary" />
                    </div>
                    <h3 className="loading-text">Loading Dashboard</h3>
                    <p className="loading-subtext">Preparing your content insights...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="dashboard-container">
            {/* Welcome Header */}
            <div className="welcome-section">
                <div className="welcome-content">
                    <div className="welcome-text">
                        <h1 className="welcome-title">
                            {getTimeBasedGreeting()}, <span className="user-name">{user?.name || 'Admin'}</span>
                        </h1>
                        <p className="welcome-subtitle">
                            {siteData ? (
                                <>Great things are happening at <strong className="site-name">{siteData.brand_name}</strong> today!</>
                            ) : (
                                "Let's make the most out of your dashboard!"
                            )}
                        </p>
                        {siteData?.http_link && (
                            <div className="site-url-display">
                                <i className="fas fa-link"></i>
                                <span className="url">{siteData.http_link}</span>
                            </div>
                        )}
                    </div>
                    <div className="welcome-actions">
                        <Button 
                            variant="outline-light" 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="refresh-btn"
                        >
                            <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                        </Button>
                        <Button 
                            variant="primary" 
                            as={Link} 
                            to="/site-admin/posts/create"
                            className="create-post-btn"
                        >
                            <i className="fas fa-plus"></i>
                            New Post
                        </Button>
                        {siteData?.http_link && (
                            <Button 
                                variant="success" 
                                onClick={handleViewSite}
                                className="visit-site-btn"
                            >
                                <i className="fas fa-external-link-alt"></i>
                                Visit Site
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" className="dashboard-alert">
                    <div className="alert-content">
                        <i className="fas fa-exclamation-triangle"></i>
                        <div className="alert-text">
                            <strong>Oops!</strong> {error}
                        </div>
                        <Button 
                            variant="outline-danger" 
                            size="sm" 
                            onClick={() => fetchDashboardData()}
                        >
                            Try Again
                        </Button>
                    </div>
                </Alert>
            )}

            {/* Stats Grid */}
            <Row className="stats-grid">
                {dashboardStats.map((stat, index) => (
                    <Col key={index} xs={12} sm={6} xl={3} className="mb-4">
                        <Card className="stat-card">
                            <Card.Body>
                                <div className="stat-content">
                                    <div className="stat-info">
                                        <h3 className="stat-value">{stat.value.toLocaleString()}</h3>
                                        <p className="stat-title">{stat.title}</p>
                                        <div className="stat-trend">
                                            <span className="stat-desc">{stat.description}</span>
                                        </div>
                                    </div>
                                    <div 
                                        className="stat-icon"
                                        style={{ background: stat.gradient }}
                                    >
                                        <i className={`fas ${stat.icon}`}></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Recent Posts & Activity */}
            <Row>
                <Col lg={8}>
                    <Card className="recent-posts-card">
                        <Card.Header className="card-header-modern">
                            <div className="header-content">
                                <h5 className="card-title">
                                    <i className="fas fa-clock"></i>
                                    Recent Posts
                                </h5>
                                <span className="post-count">{posts.length} posts</span>
                            </div>
                            <Button 
                                as={Link} 
                                to="/site-admin/posts" 
                                variant="outline-primary" 
                                size="sm"
                                className="view-all-btn"
                            >
                                View All
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {posts.length > 0 ? (
                                <div className="posts-list">
                                    {posts.map((post) => (
                                        <div key={post.id} className="post-item">
                                            <div className="post-content">
                                                <div className="post-main">
                                                    <h6 className="post-title">{post.title || 'Untitled Post'}</h6>
                                                    <p className="post-excerpt">
                                                        {post.post ? 
                                                            post.post.replace(/<[^>]*>/g, '').substring(0, 120) + '...' : 
                                                            'No content available'
                                                        }
                                                    </p>
                                                    <div className="post-meta">
                                                        <span className="post-date">
                                                            <i className="fas fa-calendar"></i>
                                                            {formatDate(post.created_at)}
                                                        </span>
                                                        <span className="post-views">
                                                            <i className="fas fa-eye"></i>
                                                            {post.views || 0} views
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="post-actions">
                                                    <Badge 
                                                        bg={getStatusVariant(post.status)} 
                                                        className="status-badge"
                                                    >
                                                        {getStatusText(post.status)}
                                                    </Badge>
                                                    <div className="action-buttons">
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            as={Link}
                                                            to={`/site-admin/posts/edit/${post.id}`}
                                                            className="action-btn-sm"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </Button>
                                                        {post.status === 1 && (
                                                            <Button 
                                                                variant="outline-success" 
                                                                size="sm"
                                                                onClick={() => handleViewPost(post)}
                                                                className="action-btn-sm"
                                                                title="View on Website"
                                                            >
                                                                <i className="fas fa-external-link-alt"></i>
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <i className="fas fa-newspaper"></i>
                                    </div>
                                    <h5>No posts yet</h5>
                                    <p>Start creating amazing content for your website</p>
                                    <Button 
                                        as={Link} 
                                        to="/site-admin/posts/create" 
                                        variant="primary"
                                        className="empty-action-btn"
                                    >
                                        <i className="fas fa-plus"></i>
                                        Create Your First Post
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Sidebar - Website Info & Quick Stats */}
                <Col lg={4}>
                    <Card className="website-info-card">
                        <Card.Header className="card-header-modern">
                            <h5 className="card-title">
                                <i className="fas fa-globe"></i>
                                Website Info
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {siteData ? (
                                <div className="website-info">
                                    <div className="website-brand">
                                        <div className="brand-icon">
                                            <i className="fas fa-star"></i>
                                        </div>
                                        <div className="brand-info">
                                            <h6 className="brand-name">{siteData.brand_name}</h6>
                                            {siteData.http_link && (
                                                <p className="website-url">
                                                    <i className="fas fa-link"></i>
                                                    {siteData.http_link}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="website-stats">
                                        <div className="stat-item">
                                            <div className="stat-label">Posts</div>
                                            <div className="stat-value">{stats.totalPosts}</div>
                                        </div>
                                        <div className="stat-item">
                                            <div className="stat-label">Published</div>
                                            <div className="stat-value">{stats.publishedPosts}</div>
                                        </div>
                                        <div className="stat-item">
                                            <div className="stat-label">Drafts</div>
                                            <div className="stat-value">{stats.draftPosts}</div>
                                        </div>
                                    </div>
                                    
                                    <Button 
                                        variant="primary" 
                                        onClick={handleViewSite}
                                        className="website-action-btn"
                                    >
                                        <i className="fas fa-external-link-alt"></i>
                                        Visit Website
                                    </Button>
                                </div>
                            ) : (
                                <div className="no-website-info">
                                    <i className="fas fa-info-circle"></i>
                                    <p>No website information available</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Quick Tips Card */}
                    <Card className="tips-card">
                        <Card.Header className="card-header-modern">
                            <h5 className="card-title">
                                <i className="fas fa-lightbulb"></i>
                                Quick Tips
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="tips-list">
                                <div className="tip-item">
                                    <i className="fas fa-check-circle text-success"></i>
                                    <span>Keep your posts updated regularly</span>
                                </div>
                                <div className="tip-item">
                                    <i className="fas fa-check-circle text-success"></i>
                                    <span>Use relevant images in your posts</span>
                                </div>
                                <div className="tip-item">
                                    <i className="fas fa-check-circle text-success"></i>
                                    <span>Optimize for SEO with good titles</span>
                                </div>
                                <div className="tip-item">
                                    <i className="fas fa-check-circle text-success"></i>
                                    <span>Engage with your audience</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Dashboard;